import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { aiAutomationApi } from '../../utils/aiAutomationApi';

const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

const fmtDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return value;
  }
};

const fmtDuration = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  const seconds = Number(value);
  if (seconds < 1) return `${seconds.toFixed(2)}s`;
  if (seconds < 10) return `${seconds.toFixed(1)}s`;
  return `${Math.round(seconds)}s`;
};

const fmtCost = (value) => {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '-';
  return `$${Number(value).toFixed(6)}`;
};

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const StatusBadge = ({ status }) => {
  const isError = status === 'error';
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${isError ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'}`}>
      {isError ? 'Error' : 'Success'}
    </span>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className="mt-1 break-words text-sm text-slate-800 dark:text-slate-100">{value || '-'}</div>
  </div>
);

export default function AdminLangfusePage() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filters, setFilters] = useState({
    start_date: daysAgo(7),
    end_date: today(),
    status: '',
    provider: '',
    model: '',
    tier: '',
    endpoint: '',
    session_id: '',
    search_id: '',
  });

  const pageSize = 50;
  const totalPages = useMemo(() => Math.max(1, Math.ceil((Number(total) || items.length || 1) / pageSize)), [total, items.length]);

  const setFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const buildQuery = useCallback((targetPage = page) => {
    const params = new URLSearchParams({
      page: String(targetPage),
      limit: String(pageSize),
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    return params.toString();
  }, [filters, page]);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await aiAutomationApi.get('/admin/langfuse/status');
      setStatus(response);
    } catch (err) {
      setStatus({ configured: false, enabled: false });
    }
  }, []);

  const fetchGenerations = useCallback(async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const response = await aiAutomationApi.get(`/admin/langfuse/generations?${buildQuery(targetPage)}`, { timeout: 120000 });
      setItems(response?.data || []);
      setTotal(response?.total || response?.data?.length || 0);
      setPage(targetPage);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setError(err?.message || 'Failed to load Langfuse traces');
    } finally {
      setLoading(false);
    }
  }, [buildQuery, page]);

  const openDetail = async (item) => {
    setSelected(item);
    if (!item?.id) return;
    setDetailLoading(true);
    try {
      const response = await aiAutomationApi.get(`/admin/langfuse/generations/${encodeURIComponent(item.id)}`, { timeout: 120000 });
      setSelected(response || item);
    } catch {
      setSelected(item);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadCsv = () => {
    const headers = [
      'id',
      'traceId',
      'type',
      'name',
      'startTime',
      'endTime',
      'level',
      'statusMessage',
      'modelId',
      'providedModelName',
      'provider',
      'tier',
      'role',
      'usageDetails',
      'costDetails',
      'totalCost',
      'metadata',
      'latencyMs',
      'durationSeconds',
      'sessionId',
      'searchId',
      'userId',
      'companyId',
    ];

    const rows = items.map((item) => [
      item.id,
      item.trace_id,
      item.type,
      item.name,
      item.started_at,
      item.ended_at,
      item.status === 'error' ? 'ERROR' : 'DEFAULT',
      item.status_message,
      item.model_id,
      item.provided_model_name || item.model,
      item.provider,
      item.tier,
      item.role,
      {
        input: item.input_tokens || 0,
        output: item.output_tokens || 0,
        total: item.total_tokens || 0,
      },
      item.cost_details || {},
      item.total_cost,
      item.metadata || {},
      item.latency_ms,
      item.duration_seconds,
      item.session_id,
      item.search_id,
      item.user_id,
      item.company_id,
    ]);

    const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `langfuse-llm-traces-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    fetchGenerations(1);
  }, [fetchGenerations]);

  const summary = useMemo(() => {
    const totalTokens = items.reduce((sum, item) => sum + Number(item.total_tokens || 0), 0);
    const errors = items.filter((item) => item.status === 'error').length;
    const slowest = items.reduce((max, item) => Math.max(max, Number(item.duration_seconds || 0)), 0);
    return { totalTokens, errors, slowest };
  }, [items]);

  return (
    <div className="space-y-6 px-4 pb-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">LLM Traces</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Admin-only Langfuse dashboard for AI calls, model usage, latency, and failures.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={downloadCsv}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            disabled={loading || items.length === 0}
          >
            Download CSV
          </button>
          <button
            type="button"
            onClick={() => fetchGenerations(page)}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {status && !status.configured && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
          Langfuse is not configured. Add <strong>LANGFUSE_PUBLIC_KEY</strong>, <strong>LANGFUSE_SECRET_KEY</strong>, and optional <strong>LANGFUSE_HOST</strong> in the AI automation backend .env.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="text-xs font-semibold uppercase text-slate-400">Loaded Calls</div>
          <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{items.length}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="text-xs font-semibold uppercase text-slate-400">Tokens</div>
          <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{summary.totalTokens.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="text-xs font-semibold uppercase text-slate-400">Errors</div>
          <div className="mt-2 text-2xl font-bold text-red-600">{summary.errors}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="text-xs font-semibold uppercase text-slate-400">Slowest Loaded</div>
          <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{fmtDuration(summary.slowest)}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Start Date
            <input type="date" value={filters.start_date} onChange={(e) => setFilter('start_date', e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950" />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">End Date
            <input type="date" value={filters.end_date} onChange={(e) => setFilter('end_date', e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950" />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Status
            <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950">
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Tier
            <select value={filters.tier} onChange={(e) => setFilter('tier', e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950">
              <option value="">All</option>
              <option value="HEAVY">Heavy</option>
              <option value="MID">Mid</option>
              <option value="LIGHT">Light</option>
              <option value="EMBEDDING">Embedding</option>
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Provider
            <input value={filters.provider} onChange={(e) => setFilter('provider', e.target.value)} placeholder="openai, openrouter..." className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950" />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Model
            <input value={filters.model} onChange={(e) => setFilter('model', e.target.value)} placeholder="gpt-4o, deepseek..." className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950" />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Endpoint
            <input value={filters.endpoint} onChange={(e) => setFilter('endpoint', e.target.value)} placeholder="resume_parser" className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950" />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Session ID
            <input value={filters.session_id} onChange={(e) => setFilter('session_id', e.target.value)} placeholder="session_..." className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950" />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200 md:col-span-2">Search ID
            <input value={filters.search_id} onChange={(e) => setFilter('search_id', e.target.value)} placeholder="search_..." className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950" />
          </label>
          <div className="flex items-end gap-2 md:col-span-2">
            <button type="button" onClick={() => fetchGenerations(1)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">Apply Filters</button>
            <button type="button" onClick={() => setFilters({ start_date: daysAgo(7), end_date: today(), status: '', provider: '', model: '', tier: '', endpoint: '', session_id: '', search_id: '' })} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800">Reset</button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">{error}</div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="min-w-[1400px] divide-y divide-slate-200 text-sm dark:divide-slate-700">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3">ID / Trace</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Start Time</th>
                <th className="px-4 py-3">End Time</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Provider</th>
                <th className="px-4 py-3">Model ID</th>
                <th className="px-4 py-3">Provided Model</th>
                <th className="px-4 py-3">Usage</th>
                <th className="px-4 py-3">Cost</th>
                <th className="px-4 py-3">Latency</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-slate-500" colSpan="13">Loading Langfuse traces...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-slate-500" colSpan="13">No LLM traces found for the selected filters.</td></tr>
              ) : items.map((item) => (
                <tr key={item.id || `${item.trace_id}-${item.started_at}`} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/70" onClick={() => openDetail(item)}>
                  <td className="px-4 py-3">
                    <div className="max-w-[150px] truncate font-mono text-xs font-semibold text-slate-800 dark:text-slate-100">{item.id || '-'}</div>
                    <div className="max-w-[150px] truncate font-mono text-xs text-slate-400">{item.trace_id || '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{item.type || 'GENERATION'}</td>
                  <td className="px-4 py-3">
                    <div className="max-w-[220px] truncate font-semibold text-slate-900 dark:text-white">{item.name || 'unknown'}</div>
                    <div className="max-w-[220px] truncate text-xs text-slate-400">{item.session_id || item.search_id || '-'}</div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-200">{fmtDateTime(item.started_at)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-200">{fmtDateTime(item.ended_at)}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{item.tier || '-'}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-200">{item.provider || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="max-w-[220px] truncate text-slate-700 dark:text-slate-200">{item.model_id || item.model || '-'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="max-w-[220px] truncate text-slate-700 dark:text-slate-200">{item.provided_model_name || item.model || '-'}</div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-200">
                    <div>Total: {Number(item.total_tokens || 0).toLocaleString()}</div>
                    <div className="text-xs text-slate-400">In {Number(item.input_tokens || 0).toLocaleString()} / Out {Number(item.output_tokens || 0).toLocaleString()}</div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-200">{fmtCost(item.total_cost)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-200">{item.latency_ms ? `${item.latency_ms}ms` : fmtDuration(item.duration_seconds)}</td>
                  <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm dark:border-slate-700">
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1 || loading} onClick={() => fetchGenerations(page - 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50 dark:border-slate-700">Previous</button>
            <button type="button" disabled={loading || (totalPages && page >= totalPages)} onClick={() => fetchGenerations(page + 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50 dark:border-slate-700">Next</button>
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setSelected(null)}>
          <aside className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-xl dark:bg-slate-950" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">{selected.name || 'LLM Call'}</h2>
                <p className="mt-1 text-sm text-slate-500">{detailLoading ? 'Loading detail...' : selected.id}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">Close</button>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field label="Status" value={selected.status} />
              <Field label="Duration" value={fmtDuration(selected.duration_seconds)} />
              <Field label="Provider" value={selected.provider} />
              <Field label="Model" value={selected.model} />
              <Field label="Tier" value={selected.tier} />
              <Field label="Role" value={selected.role} />
              <Field label="Input Tokens" value={selected.input_tokens?.toLocaleString?.() || selected.input_tokens} />
              <Field label="Output Tokens" value={selected.output_tokens?.toLocaleString?.() || selected.output_tokens} />
              <Field label="Session ID" value={selected.session_id} />
              <Field label="Search ID" value={selected.search_id} />
              <Field label="Trace ID" value={selected.trace_id} />
              <Field label="User ID" value={selected.user_id} />
            </div>

            {selected.status_message && (
              <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                {selected.status_message}
              </div>
            )}

            <div className="mt-6">
              <h3 className="mb-2 text-sm font-bold text-slate-900 dark:text-white">Metadata</h3>
              <pre className="max-h-80 overflow-auto rounded-xl bg-slate-950 p-4 text-xs text-slate-100">{JSON.stringify(selected.metadata || {}, null, 2)}</pre>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
